import DOMPurify from 'dompurify'
import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Image } from '@mui/icons-material'
import { Avatar, Box, Stack, Typography } from '@mui/material'

import { GRTable, GRTableColumn } from '../../../../../components/GRTable/GRTable'
import { rowActionsColumn } from '../../../../components/RowActions/RowActions'
import { ExampleItemValues } from '../helpers/featureHelpers'

const imageHeight = 80

type CustomProps = {
  onEdit: (index: number) => void
  onRemove: (index: number) => void
}

type ExampleItemsTableProps = CustomProps & {
  items: ExampleItemValues[]
  onReorder: (items: ExampleItemValues[]) => void
}

export const ExampleItemsTable: FC<ExampleItemsTableProps> = ({ items, onEdit, onRemove, onReorder }) => {
  const { t } = useTranslation()

  const tableColumns: GRTableColumn<ExampleItemValues, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-analysts:categories_example_image'),
        accessor: ({ row }) =>
          row.image?.url ? (
            <Box component="img" src={row.image.url} alt={row.titles.en} sx={{ height: imageHeight, maxWidth: imageHeight * 2, objectFit: 'contain' }} />
          ) : (
            <Avatar variant="rounded" sx={{ width: imageHeight, height: imageHeight }}>
              <Image />
            </Avatar>
          ),
        headerCellProps: { sx: { width: 180, minWidth: 180 } },
      },
      {
        labelAccessor: t('internal-analysts:categories_example_item'),
        accessor: ({ row }) => (
          <Stack spacing={0.5} textAlign="left">
            {row.image && (
              <Typography variant="body2" color="text.secondary">
                {t('internal-analysts:categories_example_image_copyright')}: {row.imageCopyright || '---'}
              </Typography>
            )}
            <Typography fontWeight="bold">{row.titles.en}</Typography>
            <Box sx={{ '& p': { m: 0 } }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(row.descriptions.en ?? '') }} />
          </Stack>
        ),
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left' },
      },
      rowActionsColumn<ExampleItemValues, CustomProps>({
        actions: ({ row, rowIndex, customTableProps }) => [
          {
            type: 'edit',
            label: t('internal-analysts:categories_example_item_edit'),
            ariaLabel: t('internal-analysts:categories_example_item_edit_named', { title: row.titles.en }),
            onClick: () => customTableProps?.onEdit(rowIndex),
          },
          {
            type: 'delete',
            label: t('internal-analysts:categories_example_item_remove'),
            ariaLabel: t('internal-analysts:categories_example_item_remove_named', { title: row.titles.en }),
            onClick: () => customTableProps?.onRemove(rowIndex),
          },
        ],
        width: 120,
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)
  const customProps = useMemo<CustomProps>(() => ({ onEdit, onRemove }), [onEdit, onRemove])

  return (
    <GRTable
      rows={items}
      columns={columns}
      onColumnsUpdated={setColumns}
      rowIdKey={(row) => row.key}
      customProps={customProps}
      onSortRows={onReorder}
      sortable
      noRowsLabel={t('internal-analysts:categories_example_no_items')}
      striped
    />
  )
}
